import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import gtm from '../../utils/gtmUtils';

// Délai de repli avant de lire document.title quand react-helmet n'a rien recommité après
// une navigation (balises <head> identiques à la page précédente) ou quand Didomi devient
// prêt après le rendu de la page. Assez long pour que le commit helmet (rAF) soit passé.
const PAGE_TITLE_FALLBACK_DELAY = 500;

const TrackingContext = React.createContext({ ready: false, gtm });

export const useTracking = () => React.useContext(TrackingContext);

/**
 * Pousse un page_load par navigation avec le vrai titre de la page (document.title, posé par
 * HeadComponent via react-helmet) — pas de mapping route → libellé.
 *
 * Le titre d'une page peut arriver après ses données (fiche marchand, interstitiel, annuaire
 * par catégorie) : on écoute les commits de react-helmet (onChangeClientState) et on n'envoie
 * le page_load qu'une fois un titre de page présent. Tant que document.title vaut la seule
 * marque (HeadComponent sans title), la page n'est pas considérée comme affichée.
 */
const TrackingProvider = ({ children, location, brandName }) => {
    const [didomiReady, setDidomiReady] = useState(false);
    // Refs lues depuis le callback react-helmet, exécuté hors du cycle de rendu React
    const readyRef = useRef(false);
    const pathnameRef = useRef(location.pathname);
    // page_load encore à envoyer pour la page courante ; réarmé à chaque changement de
    // pathname (pendant le rendu, donc avant tout commit helmet de la nouvelle page)
    const pendingRef = useRef(true);

    if (pathnameRef.current !== location.pathname) {
        pathnameRef.current = location.pathname;
        pendingRef.current = true;
    }

    const trackPage = useCallback(() => {
        const title = document.title;
        if (!readyRef.current || !pendingRef.current || !title || title === brandName) {
            return;
        }
        pendingRef.current = false;
        gtm.page(title);
    }, [brandName]);

    // Didomi est chargé par le stub inline de public/index.html : on s'abonne via la
    // file didomiOnReady (le SDK exécute immédiatement le callback s'il est déjà prêt)
    useEffect(() => {
        window.didomiOnReady = window.didomiOnReady || [];
        window.didomiOnReady.push(() => {
            readyRef.current = true;
            setDidomiReady(true);
        });
    }, []);

    useEffect(() => {
        if (!didomiReady) {
            return undefined;
        }
        const timeout = setTimeout(trackPage, PAGE_TITLE_FALLBACK_DELAY);
        return () => clearTimeout(timeout);
    }, [didomiReady, location.pathname, trackPage]);

    return (
        <TrackingContext.Provider value={{ ready: didomiReady, gtm }}>
            <Helmet onChangeClientState={trackPage} />
            {children}
        </TrackingContext.Provider>
    );
};

const mapStateToProps = state => ({
    brandName: state.i18n.common.BRAND_NAME
});

export default withRouter(connect(mapStateToProps)(TrackingProvider));



// WEBPACK FOOTER //
// ./src/tracking/provider/index.js